<template>
  <div class="scheduling-tab">
    <dx-data-grid
      :data-source="getSchedulingDataSource"
      :columns="getSchedulingColumns"
      @toolbar-preparing="onToolbarPreparing($event)"
      :row-alternation-enabled="true"
      :columnChooser="{
        enabled: false,
      }"
      :pager="{ visible: false }"
      :height="height"
      :selection="{
        mode: 'none',
      }"
      ref="scheduling-tab-grid"
      @content-ready="onContentReady"
      @editor-preparing="onEditorPreparing"
      :focusedRowEnabled="popupMode === 'edit'"
      keyExpr="id"
      @row-removed="onRowRemoved"
      @saved="onSaved"
      @editing-start="onEditingStart"
      :column-auto-width="false"
      :element-attr="{
        class: 'scheduling-tab-grid',
      }"
    >
      <!-- <DxScrolling
        :mode="'virtual'"
        :row-rendering-mode="'virtual'"
        :preload-enabled="true"
      /> -->

      <DxKeyboardNavigation
        :edit-on-key-press="true"
        :enter-key-action="'startEdit'"
        :enter-key-direction="'none'"
        :enabled="true"
      />

      <DxEditing
        :allow-deleting="popupMode === 'edit' && isEditable"
        :allow-updating="popupMode === 'edit' && isEditable"
        :mode="'cell'"
        :changes="changes"
        :confirm-delete="false"
      />
    </dx-data-grid>
  </div>
</template>

<script>
import ArrayStore from "devextreme/data/array_store";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import dataGridMixin from "../../../_mixins/data-grid.mixin";
import { WorkOrderService } from "../../../_services/api/production/work-order.service";
import { backwardTabbing } from "../../../_helpers/ui/keyboard";

const baseLine = {
  ProcessCode: null,
  ProcessDescription: null,
  ScheduledStart: null,
  ScheduledEnd: null,
  DurationHrs: 0,
  DurationMins: 0,
  TotalProcessMinutes: 0,
  RemainingDurationMins: 0,
};

export default {
  mixins: [dataGridMixin],
  components: {},
  props: {
    dataModel: {
      type: Object,
      default: () => ({}),
    },
    height: {
      type: [Number, String],
      default: 280,
    },
    displayServerError: {
      type: Function,
      default: function () {},
    },
    showLoading: {
      type: Function,
      default: () => {},
    },
    popupMode: {
      type: String,
      default: "create",
    },
    refreshOnUpdate: {
      type: Boolean,
      default: false,
    },
    selectedTabIndex: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    shiftSchedules: {
      type: Array,
      default: () => [],
    },
    isVisibleNow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      WorkOrderInstance: "Core",
      changes: [],
      SchedulingDataSource: new ArrayStore({
        key: "id",
        data: [],
      }),
      WorkOrderProcessList: [],
    };
  },
  async created() {
    this.showLoading(true);

    if (this.shiftSchedules.length > 0) {
      this.shiftSchedules.forEach((schedule) => {
        this.SchedulingDataSource.insert({
          ...baseLine,
          WorkOrderCode: this.dataModel.WorkOrderCode,
          ProcessCode: schedule.ProcessCode,
          ProcessDescription: schedule.Process
            ? schedule.Process.Description
            : "",
          ScheduledStart: new Date(
            new Date(schedule.StartDate).toISOString().replace("Z", ""),
          ),
          ScheduledEnd: new Date(
            new Date(schedule.EndDate).toISOString().replace("Z", ""),
          ),
          DurationHrs: (schedule.AllocationTime / 60).roundNumber(2),
          DurationMins: schedule.AllocationTime,
        });
      });
    }

    if (this.popupMode === "edit" || this.shiftSchedules.length > 0) {
      this.SchedulingDataSource.insert(baseLine);
      await this.getWorkOrderProcessList();
    }
  },
  methods: {
    /**
     * get work order process list
     */
    async getWorkOrderProcessList() {
      this.WorkOrderProcessList = [];
      await this.$nextTick();
      try {
        const response = await WorkOrderService.getWorkOrderProcessList(
          this.dataModel.WorkOrderCode,
        );

        if (response && response.data && response.data.value) {
          this.WorkOrderProcessList = response.data.value;

          await this.$nextTick();
          this.updateTotalProcessMinutesInLines();
        }
      } catch (error) {
        this.displayServerError(error, "Failed to load processes");
      } finally {
        this.showLoading(false);
      }
    },

    /**
     * update total process minutes in scheduling lines
     */
    updateTotalProcessMinutesInLines() {
      const items = this.SchedulingDataSource._array;

      items.forEach((line) => {
        const process = this.WorkOrderProcessList.find(
          (p) => p.ProcessCode === line.ProcessCode,
        );

        const totalDurationMins = items
          .filter((i) => i.ProcessCode === line.ProcessCode)
          .reduce((sum, curr) => sum + (curr.DurationMins || 0), 0);

        if (process) {
          const RemainingDurationMins =
            Math.ceil(process.TotalProcessMinutes) - totalDurationMins || 0;

          this.SchedulingDataSource.update(line.id, {
            TotalProcessMinutes: Math.ceil(process.TotalProcessMinutes) || 0,
            RemainingDurationMins:
              RemainingDurationMins < 0 ? 0 : RemainingDurationMins,
          });
        }
      });

      this.$nextTick(() => {
        this.dataGrid.refresh(true);
      });
    },
    /**
     * on so list grid toolbar preparing
     * @param e - event
     */
    onToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },
    onContentReady(e) {
      this.addGridNoResults(e, {
        addNew: {
          class: "no-data",
          title: "No Process scheduled",
          message: "No Process scheduled for this Work Order",
        },
      });

      setTimeout(() => {
        this.$nextTick(() => {
          e.component.beginUpdate();
          e.component.updateDimensions();
          e.component.endUpdate();
        });
      }, 200);
    },
    onEditorPreparing(e) {
      if (e.dataField && e.dataField == "ProcessCode") {
        e.editorName = "dxSelectBox";
      }
      if (e.dataField && e.dataField == "Comment") {
        e.editorName = "dxTextArea";
      }

      if (e.parentType !== "searchPanel") {
        const { dataField, editorOptions, row } = e;
        // Only wrap handlers if not in the search panel
        if (!editorOptions) return;

        const wrapHandler = (handlerName) => {
          const original = editorOptions[handlerName];
          if (typeof original === "function") {
            editorOptions[handlerName] = (event) => {
              original(event, {
                column: { dataField },
                data: row.data,
                rowIndex: row.rowIndex,
              });
            };
          }
        };

        // Wrap each handler if it exists
        [
          "onValueChanged",
          "onItemClick",
          "onKeyDown",
          "onOpened",
          "onFocusIn",
        ].forEach(wrapHandler);
      }
    },

    async onEditingStart(e) {
      const rowData = e.data;

      if (this.popupMode !== "edit" || !this.isEditable) {
        e.cancel = true;
        return;
      }

      if (e.column.dataField !== "ProcessCode") {
        if (rowData.ProcessCode === null) {
          e.cancel = true;
          return;
        }

        if (
          e.column.dataField !== "ScheduledStart" &&
          !rowData.ScheduledStart
        ) {
          e.cancel = true;
          await this.$alert(
            `Scheduled Start is required before editing.`,
            "Validation Error",
          );

          this.focusOnEditableCell(
            this.dataGrid.getRowIndexByKey(rowData.id),
            "ScheduledStart",
          );

          return;
        }
      }

      if (e.column.dataField === "ScheduledEnd") {
        e.column.editorOptions.min = rowData.ScheduledStart;
      }
    },
    focusOnEditableCell(rowIndex, col) {
      const column = col ? col : "ProcessCode";
      const dataGrid = this.dataGrid;
      const cellElement = dataGrid.getCellElement(rowIndex, column);
      dataGrid.focus(cellElement);
      setTimeout(() => {
        dataGrid.editCell(rowIndex, column);
      }, 10);
    },

    addNewRowToGrid(refresh = false) {
      this.SchedulingDataSource.insert({
        ...baseLine,
        WorkOrderCode: this.dataModel.WorkOrderCode,
      });

      if (refresh) {
        setTimeout(() => {
          this.dataGrid.saveEditData();
          this.dataGrid.refresh(true);

          this.$nextTick(() => {
            this.dataGrid.updateDimensions();
          });
        }, 100);
      }
    },

    async onProcessCodeValueChanged(e, lineData) {
      if (!e.event) return;
      if (e.value) {
        if (lineData.data.ProcessCode === null) {
          this.addNewRowToGrid(true);
        }

        let processCode = e.value;
        const component = e.component; // the dxSelectBox instance
        const productData = component
          .option("items")
          .find((item) => item.ProcessCode === processCode);

        if (productData) {
          this.SchedulingDataSource.update(lineData.data.id, {
            ProcessCode: productData.ProcessCode,
            ProcessDescription: productData.ProcessDescription,
            TotalProcessMinutes:
              Math.ceil(productData.TotalProcessMinutes) || 0,
          });

          await this.updateRemainingDurationMins(lineData.data);

          this.$nextTick(() => {
            this.dataGrid.refresh(true);
            const rowIndex = this.dataGrid.getRowIndexByKey(lineData.data.id);
            this.focusOnEditableCell(rowIndex, "ScheduledStart");
          });
        }
      }
    },

    getTimeDiff(start, end) {
      // CLONE dates (critical!)
      const s = new Date(start);
      const e = new Date(end);

      const diffMs = new Date(e) - new Date(s); // milliseconds difference

      const minutes = Math.round(diffMs / 60000);
      const hours = +(minutes / 60).toFixed(2);

      return { hours, minutes };
    },

    setFocusOnHeaderForm() {
      this.$emit("setFocusOnField", "Comments", false, true);
    },

    async getScheduleLines() {
      const items = await this.SchedulingDataSource.load({
        filter: [["ProcessCode", "<>", null], "and", ["ProcessCode", "<>", ""]],
      });

      return items;
    },

    async updateRemainingDurationMinsAfterRowRemoved() {
      const items = await this.SchedulingDataSource.load({
        filter: [["ProcessCode", "<>", null], "and", ["ProcessCode", "<>", ""]],
      });

      for (const line of items) {
        await this.updateRemainingDurationMins(line);
      }
    },

    async updateRemainingDurationMins(lineData) {
      this.SchedulingDataSource.update(lineData.id, lineData);
      await this.$nextTick();

      const items = this.SchedulingDataSource._array;

      const processItems = items.filter(
        (p) => p.ProcessCode === lineData.ProcessCode,
      );

      if (processItems.length > 0) {
        const totalDurationMins = processItems.reduce(
          (sum, curr) => sum + (curr.DurationMins || 0),
          0,
        );

        if (totalDurationMins != undefined) {
          processItems.forEach((line) => {
            const process = this.WorkOrderProcessList.find(
              (p) => p.ProcessCode === line.ProcessCode,
            );

            if (process) {
              const RemainingDurationMins =
                Math.ceil(process.TotalProcessMinutes) - totalDurationMins ||
                0;

              this.SchedulingDataSource.update(line.id, {
                RemainingDurationMins:
                  RemainingDurationMins < 0 ? 0 : RemainingDurationMins,
              });
            }
          });
        }
      }
    },

    async showTotalDurationExceededAlert(currentRowData, column) {
      const alert = await this.$alert(
        `Duration (Mins) cannot be greater than Total Process Minutes of ${currentRowData.TotalProcessMinutes}. Only ${currentRowData.RemainingDurationMins} mins remaining. Please adjust the value.`,
        "Validation Error",
      );

      if (alert) {
        setTimeout(() => {
          this.$nextTick(() => {
            const rowIndex = this.dataGrid.getRowIndexByKey(currentRowData.id);
            this.focusOnEditableCell(rowIndex, column);
          });
        }, 100);
      }
    },

    async onRowRemoved(e) {
      if (!e) return;
      const items = await this.SchedulingDataSource.load();
      if (items.length === 0) {
        this.addNewRowToGrid();
      } else if (items[items.length - 1].ProcessCode !== null) {
        this.addNewRowToGrid();
      }

      this.$nextTick(() => {
        this.updateRemainingDurationMinsAfterRowRemoved();
      });
    },

    onSaved(e) {
      if (e.changes && e.changes.length > 0) {
        const change = e.changes[0];
        if (change.insertAfterKey !== undefined) {
          const element = change.data;
          let index = -1;

          index = this.SchedulingDataSource._array.findIndex(
            (x) => x.id === change.insertAfterKey,
          );
          this.SchedulingDataSource._array.splice(
            this.SchedulingDataSource._array.length - 1,
            1,
          );
          this.SchedulingDataSource._array.splice(index + 1, 0, element);

          this.$nextTick(() => {
            this.updateRemainingDurationMinsAfterRowRemoved();
          });

          if (index !== -1)
            setTimeout(() => {
              this.focusOnEditableCell(index + 1, "ProcessCode");
            }, 100);
        }
      }
    },

    addCopyScheduleLine(e) {
      if (e.event) {
        const data = e.row.data;

        if (!data.ProcessCode) {
          this.$alert(
            `Please select a Process to copy the schedule line.`,
            "Copy Schedule Line",
          );
          return;
        } else if (!data.ScheduledStart) {
          this.$alert(
            `Please enter Scheduled Start to copy the schedule line.`,
            "Copy Schedule Line",
          );
          return;
        } else if (!data.ScheduledEnd) {
          this.$alert(
            `Please enter Scheduled End to copy the schedule line.`,
            "Copy Schedule Line",
          );
          return;
        } else if (!data.DurationMins || data.DurationMins <= 0) {
          this.$alert(
            `Please enter Duration (Mins) greater than zero to copy the schedule line.`,
            "Copy Schedule Line",
          );
          return;
        } else if (data.RemainingDurationMins <= 0) {
          this.$alert(
            `Cannot copy schedule line as there is no remaining duration for process ${data.ProcessCode}.`,
            "Copy Schedule Line",
          );
          return;
        } else {
          const newLine = { ...data };
          delete newLine.id; // Remove ID to create a new record

          if (newLine.DurationMins > newLine.RemainingDurationMins) {
            newLine.DurationMins = newLine.RemainingDurationMins;
            const start = new Date(newLine.ScheduledStart);
            const end = new Date(
              start.getTime() + newLine.DurationMins * 60 * 1000,
            );
            newLine.ScheduledEnd = end;

            newLine.DurationHrs = (newLine.DurationMins / 60).roundNumber(2);
          }

          this.changes = [
            {
              type: "insert",
              data: newLine,
              insertAfterKey: e.row.key,
            },
          ];

          const dataGrid = this.dataGrid;
          dataGrid.clearSelection();
          dataGrid.option("focusedRowIndex", -1);

          this.$nextTick(() => {
            dataGrid.saveEditData();
            dataGrid.refresh(true);
          });
        }
      }
    },
  },
  computed: {
    dataGrid() {
      return this.$refs["scheduling-tab-grid"].instance;
    },
    getSchedulingDataSource() {
      return this.SchedulingDataSource._array;
    },
    getSchedulingColumns() {
      return [
        {
          caption: "Process",
          dataField: "ProcessCode",
          width: 100,
          allowEditing: this.popupMode !== "create" && this.isEditable,
          cssClass: "edit-cell-textbox-field",
          editorType: "dxSelectBox",
          editorOptions: {
            displayExpr: "Code",
            valueExpr: "ProcessCode",
            searchTimeout: 10,
            searchEnabled: true,
            searchExpr: ["ProcessCode", "ProcessDescription"],
            onFocusIn: async (e) => {
              const selectBox = e.component;
              selectBox.option("items", this.WorkOrderProcessList);
              await this.$nextTick();
              setTimeout(() => {
                selectBox.focus();
                this.$nextTick(() => {
                  e.event.target.select();
                });
              }, 200);
            },
            onValueChanged: (e, lineData) => {
              this.onProcessCodeValueChanged(e, lineData);
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete" id="${data.ProcessCode}" >
                <span class="auto-complete-primary">
                  ${data.ProcessCode}
                </span>
                <span class="record-secondary gc-grid-code">
                  ${data.ProcessDescription}
                </span>
              </div>`;
            },
            dropDownOptions: {
              minWidth: 400,
            },
            onKeyDown: (e, lineData) => {
              if (backwardTabbing(e) && lineData.rowIndex === 0) {
                e.event.preventDefault();
                this.setFocusOnHeaderForm();
              }
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (!currentRowData.ProcessCode) {
              this.addNewRowToGrid(true);
            }
            newData.ProcessCode = value;

            return newData;
          },
        },
        {
          caption: "Description",
          dataField: "ProcessDescription",
          minWidth: ColumnWidth.Description.minWidth,
          // width: "auto",
          allowEditing: false,
        },
        {
          caption: "Remaining Duration (Mins)",
          dataField: "RemainingDurationMins",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0",
          allowEditing: false,
        },
        {
          dataField: "ScheduledStart",
          minWidth: ColumnWidth.DateTime.minWidth + 20,
          width: ColumnWidth.DateTime.width + 20,
          dataType: "datetime",
          format: "dd MMM yyyy hh:mm:ss a",
          allowSorting: true,
          calculateDisplayValue: (e) => {
            return e.ScheduledStart;
          },
          allowEditing: this.popupMode !== "create" && this.isEditable,
          cssClass: "edit-cell-datebox-field",
          setCellValue: async (newData, value, currentRowData) => {
            newData.ScheduledStart = value;

            if (currentRowData.ScheduledEnd !== null && value !== null) {
              newData.ScheduledEnd = null;
              newData.DurationHrs = 0;
              newData.DurationMins = 0;

              await this.$alert(
                `Scheduled End cleared due to start time change.`,
                "Information",
              );

              this.updateRemainingDurationMins({
                ...currentRowData,
                ...newData,
              });

              setTimeout(() => {
                this.focusOnEditableCell(
                  this.dataGrid.getRowIndexByKey(currentRowData.id),
                  "ScheduledEnd",
                );
              }, 100);
            }

            return newData;
          },
        },
        {
          dataField: "ScheduledEnd",
          minWidth: ColumnWidth.DateTime.minWidth + 20,
          width: ColumnWidth.DateTime.width + 20,
          dataType: "datetime",
          format: "dd MMM yyyy hh:mm:ss a",
          allowSorting: true,
          calculateDisplayValue: (e) => {
            return e.ScheduledEnd;
          },
          allowEditing: this.popupMode !== "create" && this.isEditable,
          cssClass: "edit-cell-datebox-field",
          setCellValue: async (newData, value, currentRowData) => {
            if (value && value < currentRowData.ScheduledStart) {
              await this.$alert(
                `Scheduled End cannot be earlier than Scheduled Start.`,
                "Validation Error",
              );

              setTimeout(() => {
                this.focusOnEditableCell(
                  this.dataGrid.getRowIndexByKey(currentRowData.id),
                  "ScheduledEnd",
                );
              }, 100);
              return;
            }

            newData.ScheduledEnd = value;

            if (currentRowData.ScheduledStart !== null && value !== null) {
              const { hours, minutes } = this.getTimeDiff(
                currentRowData.ScheduledStart,
                value,
              );

              newData.DurationHrs = hours.roundNumber(2);
              newData.DurationMins = minutes;

              if (
                minutes >
                currentRowData.RemainingDurationMins +
                  currentRowData.DurationMins
              ) {
                newData.ScheduledEnd = currentRowData.ScheduledEnd;
                newData.DurationHrs = currentRowData.DurationHrs;
                newData.DurationMins = currentRowData.DurationMins;

                this.SchedulingDataSource.update(currentRowData.id, newData);

                this.showTotalDurationExceededAlert(
                  currentRowData,
                  "ScheduledEnd",
                );
                return;
              }
              this.updateRemainingDurationMins({
                ...currentRowData,
                ...newData,
              });
            }

            if (!value) {
              newData.DurationHrs = 0;
              newData.DurationMins = 0;
              this.updateRemainingDurationMins({
                ...currentRowData,
                ...newData,
              });
            }

            return newData;
          },
          editorOptions: {},
        },

        {
          caption: "Duration (Hrs)",
          dataField: "DurationHrs",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0.00",
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "##,###,###,###,##0.00",
            min: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.DurationHrs = value;
            if (currentRowData.ScheduledStart !== null) {
              const start = new Date(currentRowData.ScheduledStart);
              const end = new Date(start.getTime() + value * 60 * 60 * 1000);
              newData.ScheduledEnd = end;

              const { minutes } = this.getTimeDiff(
                currentRowData.ScheduledStart,
                end,
              );

              newData.DurationMins = minutes;

              if (
                minutes >
                currentRowData.RemainingDurationMins +
                  currentRowData.DurationMins
              ) {
                newData.ScheduledEnd = currentRowData.ScheduledEnd;
                newData.DurationHrs = currentRowData.DurationHrs;
                newData.DurationMins = currentRowData.DurationMins;

                this.SchedulingDataSource.update(currentRowData.id, newData);

                this.showTotalDurationExceededAlert(
                  currentRowData,
                  "DurationHrs",
                );
                return;
              }

              this.updateRemainingDurationMins({
                ...currentRowData,
                ...newData,
              });
            }

            return newData;
          },
          allowEditing: this.popupMode !== "create" && this.isEditable,
        },
        {
          caption: "Duration (Mins)",
          dataField: "DurationMins",
          dataType: "number",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          format: "##,###,###,###,##0",
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "##,###,###,###,##0",
            min: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (
              value >
              currentRowData.RemainingDurationMins + currentRowData.DurationMins
            ) {
              this.showTotalDurationExceededAlert(
                currentRowData,
                "DurationMins",
              );
              return;
            } else {
              newData.DurationMins = value;

              if (currentRowData.ScheduledStart !== null) {
                const start = new Date(currentRowData.ScheduledStart);
                const end = new Date(start.getTime() + value * 60 * 1000);
                newData.ScheduledEnd = end;

                newData.DurationHrs = (value / 60).roundNumber(2);
                this.updateRemainingDurationMins({
                  ...currentRowData,
                  ...newData,
                });
              }
              return newData;
            }
          },
          allowEditing: this.popupMode !== "create" && this.isEditable,
        },
        {
          dataField: "",
          caption: "",
          type: "buttons",
          buttons: [
            {
              name: "copy",
              icon: "copy",
              onClick: (e) => {
                const activeElement = document.activeElement;
                if (activeElement) {
                  activeElement.blur();
                  this.dataGrid.saveEditData();
                }
                setTimeout(() => {
                  this.addCopyScheduleLine(e);
                }, 100);
              },
            },
            {
              hint: "Delete",
              icon: "trash",

              visible: this.popupMode !== "create" && this.isEditable,
              onClick: async (e) => {
                const confirmDelete = await this.$confirm(
                  "Are you sure you want to delete this entry?",
                  "Delete Entry",
                );
                if (confirmDelete) {
                  this.dataGrid.deleteRow(e.row.rowIndex);
                }
              },
            },
          ],
          visible: this.popupMode === "edit",
          width: 50,
          minWidth: 50,
        },
      ];
    },
  },
  watch: {
    refreshOnUpdate(newValue) {
      if (newValue) {
        this.getWorkOrderProcessList();
      }
    },
    popupMode(newValue) {
      if (newValue === "edit" && this.isEditable) {
        this.getWorkOrderProcessList();
        this.addNewRowToGrid();
      }
    },
    isVisibleNow: {
      handler(value) {
        if (value) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.dataGrid.updateDimensions();
            }, 150);
          });
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.scheduling-tab {
  .alert-row {
    background-color: #ffcccc; /* Light red background */
    // color: #a94442; /* Dark red text */
  }

  .dx-datagrid .dx-datagrid-content {
    .dx-datagrid-table .dx-row .dx-editor-cell {
      padding-right: 5px !important;
      padding-left: 5px !important;
    }

    // .dx-datebox {
    //   .dx-placeholder::before {
    //     padding-left: unset !important;
    //     padding-bottom: unset !important;
    //     padding-top: unset !important;
    //   }
    // }

    // .dx-datebox.dx-texteditor:not(.dx-state-readonly)::after {
    //   content: "";
    //   border-bottom: 1px solid rgba(0, 0, 0, 0.42) !important;
    // }
  }
}
</style>
